import { Box, Button, Flex, Heading, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Text, useDisclosure, useToast, VStack, FormControl, FormLabel, Input } from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import WelcomeSection from "components/WelcomeSection";
import { ReactTable } from "components/ReactTable";
import { createColumnHelper } from "@tanstack/react-table";
import { useTrackingAdminList } from "hooks/useTrackingAdminList";
import {
  fetchStudentUnavailabilityHistory,
  createStudentUnavailabilityHistory,
  updateStudentUnavailabilityHistory,
  deleteStudentUnavailabilityHistory,
  type StudentUnavailabilityHistoryRow,
  type StudentUnavailabilityHistoryPayload,
} from "services/tracking/trackingAdminService";
import { useAuthentication } from "hooks/useAuthentication";
import { showToast, toastTypes } from "utils";

export default function TrackingStudentUnavailabilityHistoryPage() {
  const toast = useToast();
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;
  const { data: list, isLoading, error, refetch } = useTrackingAdminList("student-unavailability-history", fetchStudentUnavailabilityHistory);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [form, setForm] = useState<StudentUnavailabilityHistoryPayload>({ student_id: 0, reason: "", start_date: "", end_date: null });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = useCallback(() => {
    setEditingId(null);
    setForm({ student_id: 0, reason: "", start_date: "", end_date: null });
    onOpen();
  }, [onOpen]);

  const handleEdit = useCallback(
    (row: StudentUnavailabilityHistoryRow) => {
      setEditingId(row.id);
      setForm({
        student_id: row.student_id,
        parent_id: row.parent_id ?? undefined,
        reason: row.reason,
        start_date: row.start_date,
        end_date: row.end_date ?? undefined,
      });
      onOpen();
    },
    [onOpen]
  );

  const handleSubmit = useCallback(async () => {
    if (!form.reason.trim() || !form.start_date) {
      showToast({ toast, message: "Raison et date de début obligatoires.", status: toastTypes.error });
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await updateStudentUnavailabilityHistory(token, editingId, form);
        showToast({ toast, message: "Enregistrement mis à jour.", status: toastTypes.success });
      } else {
        await createStudentUnavailabilityHistory(token, form);
        showToast({ toast, message: "Enregistrement créé.", status: toastTypes.success });
      }
      refetch();
      onClose();
    } catch (e: any) {
      showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error });
    } finally {
      setSubmitting(false);
    }
  }, [token, form, editingId, refetch, onClose, toast]);

  const col = createColumnHelper<StudentUnavailabilityHistoryRow>();
  const columns = useMemo(
    () => [
      col.accessor("student_id", { header: "Élève (ID)", cell: (c) => <Text>{c.getValue()}</Text> }),
      col.accessor("reason", { header: "Motif", cell: (c) => <Text fontSize="sm">{c.getValue()}</Text> }),
      col.accessor("start_date", { header: "Début", cell: (c) => <Text>{c.getValue()}</Text> }),
      col.accessor("end_date", { header: "Fin", cell: (c) => <Text>{c.getValue() ?? "—"}</Text> }),
      col.display({
        id: "action",
        header: "Action",
        cell: ({ row }) => (
          <Button size="sm" onClick={() => handleEdit(row.original)}>Modifier</Button>
        ),
      }),
    ],
    [handleEdit]
  );

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" px={{ base: "20px", sm: "24px" }}>
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Heading size="text2xl" as="h5" fontFamily="Arimo">Historique indisponibilité</Heading>
            <Button colorScheme="blue" onClick={handleCreate}>Ajouter</Button>
          </Flex>
          {error && <Box p={4} bg="red.50" borderRadius="md"><Text color="red.700">Erreur chargement.</Text></Box>}
          <Box bg="white" borderRadius="10px">
            {isLoading ? <Flex justify="center" py={10}><Spinner /></Flex> : <ReactTable columns={columns as any} data={list} size="sm" />}
            {!isLoading && !list.length && <Box p={8} textAlign="center"><Text color="gray.600">Aucune donnée.</Text></Box>}
          </Box>
        </Flex>
      </Flex>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? "Modifier" : "Ajouter"} une indisponibilité</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <FormControl><FormLabel>ID élève</FormLabel><Input type="number" value={form.student_id || ""} onChange={(e) => setForm((p) => ({ ...p, student_id: parseInt(e.target.value, 10) || 0 }))} /></FormControl>
              <FormControl><FormLabel>Motif</FormLabel><Input value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} /></FormControl>
              <FormControl><FormLabel>Date début</FormLabel><Input type="date" value={form.start_date} onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))} /></FormControl>
              <FormControl><FormLabel>Date fin (optionnel)</FormLabel><Input type="date" value={form.end_date ?? ""} onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value || null }))} /></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting}>Enregistrer</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
